import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Rocket, Globe, Loader2, Sparkles, Save, ExternalLink, Copy, Check,
} from "lucide-react";
import SrcPreviewFrame from "@/components/dashboard/SrcPreviewFrame";
import { PILL, Input, TextareaField, FieldLabel, EditorSection } from "./shared";

const SRC_TEMPLATES = new Set(["service", "restaurant", "ecommerce"]);

export default function WebsiteTab({ biz, setBiz, plan, onUpgrade }) {
  const [content, setContent] = useState(biz.website_content);
  const [slug, setSlug] = useState(biz.slug);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [previewMode, setPreviewMode] = useState("desktop");
  const [copied, setCopied] = useState(false);

  // Iter 69 — SRC preview payload. Fetched for the three SRC template
  // families; anything else shows a "preview unavailable" placeholder. The
  // payload comes from the exact same endpoint the public site consumes
  // (`GET /api/public/site/:slug`), so the preview mirrors what a visitor
  // sees for saved content. Draft businesses are supported by the endpoint
  // (only `status="disabled"` businesses are excluded), so this works
  // before the first publish too.
  const isSrc = SRC_TEMPLATES.has(biz.industry_template);
  const [srcPayload, setSrcPayload] = useState(null);
  const [srcPayloadNonce, setSrcPayloadNonce] = useState(0);
  const refreshSrcPayload = () => setSrcPayloadNonce((n) => n + 1);

  useEffect(() => {
    if (!isSrc || !biz.slug) { setSrcPayload(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/public/site/${biz.slug}`);
        if (!cancelled) setSrcPayload(data);
      } catch {
        if (!cancelled) setSrcPayload(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isSrc, biz.slug, srcPayloadNonce]);

  const publicUrl = `${window.location.origin}/site/${biz.slug}`;
  // Paid plans must publish on a custom domain — no subdomain.
  const planAllowsSubdomain = plan?.free_subdomain ?? true;
  const hasCustomDomain = !!biz?.custom_domain;
  const alreadyLive = !!biz?.first_published_at;
  const needsDomainToPublish = !planAllowsSubdomain && !hasCustomDomain && !alreadyLive;

  const updateField = (k, v) => setContent((c) => ({ ...c, [k]: v }));
  const updateService = (i, k, v) =>
    setContent((c) => ({
      ...c,
      services: c.services.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)),
    }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/business/website", content);
      toast.success("Saved");
      setBiz({ ...biz, website_content: content });
      // Iter 69 — refetch the SRC preview payload so the compiled
      // service_config / restaurant_config / ecommerce_config reflects the
      // just-saved fields.
      if (isSrc) refreshSrcPayload();
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const publish = async () => {
    setPublishing(true);
    try {
      const { data } = await api.post("/business/publish", { slug });
      setBiz(data);
      if (isSrc) refreshSrcPayload();
      toast.success("Published!");
    } catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); }
    setPublishing(false);
  };

  const regen = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.post("/business/regenerate/website");
      setBiz(data);
      setContent(data.website_content);
      if (isSrc) refreshSrcPayload();
      toast.success("Website regenerated");
    } catch { toast.error("Failed"); }
    setRegenerating(false);
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <div className="nb-card p-5 mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-display text-2xl font-extrabold">Your website</div>
          <div className="text-sm text-gray-600 mt-1">
            Status: <span className={`font-bold ${biz.status === "published" ? "text-[#10B981]" : "text-gray-700"}`}>{biz.status}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={regen} disabled={regenerating} className={`${PILL} bg-[#F5F0FF]`} data-testid="regen-website-btn">
            {regenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Regenerate
          </button>
          <button onClick={save} disabled={saving} className={`${PILL} bg-white`} data-testid="save-website-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
          <button
            onClick={publish}
            disabled={publishing || needsDomainToPublish}
            title={needsDomainToPublish ? "Connect a custom domain to publish on your plan" : undefined}
            className={`${PILL} ${needsDomainToPublish ? "bg-gray-300 cursor-not-allowed opacity-70" : "bg-[#10B981] text-white"}`}
            data-testid="publish-btn"
          >
            {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}{" "}
            {biz.status === "published" ? "Re-publish" : "Publish"}
          </button>
        </div>
      </div>

      {needsDomainToPublish && (
        <div className="nb-card p-4 mb-6 bg-[#FFF7E6] flex flex-wrap items-center justify-between gap-3" data-testid="needs-domain-banner">
          <div className="flex items-center gap-2 min-w-0">
            <Globe className="w-5 h-5 shrink-0 text-amber-700" />
            <div className="text-sm">
              <b>Your {plan?.plan_name} plan requires a custom domain to publish.</b>{" "}
              Connect one from the Domains tab to go live.
            </div>
          </div>
          <button onClick={onUpgrade} className={`${PILL} bg-white`} data-testid="needs-domain-go-billing">
            See plans
          </button>
        </div>
      )}

      {biz.status === "published" && (
        <div className="nb-card p-4 mb-6 bg-[#F0FFF4] flex flex-wrap items-center justify-between gap-3" data-testid="live-banner">
          <div className="flex items-center gap-2 min-w-0">
            <Globe className="w-5 h-5 shrink-0" />
            <a href={publicUrl} target="_blank" rel="noreferrer" className="font-mono text-sm truncate underline">{publicUrl}</a>
          </div>
          <div className="flex gap-2">
            <button onClick={copyUrl} className={`${PILL} bg-white`} data-testid="copy-url-btn">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? "Copied" : "Copy"}
            </button>
            <a href={publicUrl} target="_blank" rel="noreferrer" className={`${PILL} bg-white`}>
              <ExternalLink className="w-4 h-4" />Open
            </a>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-[420px_1fr] gap-6">
        <div className="space-y-4">
          <EditorSection title="Slug / URL" testId="slug-editor">
            <FieldLabel>Subdomain slug</FieldLabel>
            <div className="flex items-center gap-2">
              <input data-testid="slug-input" value={slug} onChange={(e) => setSlug(e.target.value)}
                className="flex-1 px-3 py-2 border-2 border-black rounded-lg text-sm font-mono" />
              <span className="text-xs text-gray-500">.interesttoincome.app</span>
            </div>
            <p className="text-xs text-gray-500 mt-2">Click Publish above to apply.</p>
          </EditorSection>

          <EditorSection title="Hero">
            <FieldLabel>Logo text</FieldLabel>
            <Input value={content.logo_text} onChange={(e) => updateField("logo_text", e.target.value)} testId="ed-logo" />
            <FieldLabel>Headline</FieldLabel>
            <Input value={content.hero_title} onChange={(e) => updateField("hero_title", e.target.value)} testId="ed-title" />
            <FieldLabel>Subheadline</FieldLabel>
            <TextareaField value={content.hero_subtitle} onChange={(e) => updateField("hero_subtitle", e.target.value)} testId="ed-subtitle" />
            <FieldLabel>Button text</FieldLabel>
            <Input value={content.hero_cta_text} onChange={(e) => updateField("hero_cta_text", e.target.value)} testId="ed-cta" />
            <FieldLabel>Hero image URL</FieldLabel>
            <Input value={content.hero_image_url || ""} onChange={(e) => updateField("hero_image_url", e.target.value)} testId="ed-hero-img" />
          </EditorSection>

          <EditorSection title="Services">
            {(content.services || []).map((s, i) => (
              <div key={i} className="p-3 border-2 border-gray-200 rounded-lg mb-2">
                <Input value={s.title} onChange={(e) => updateService(i, "title", e.target.value)} testId={`ed-svc-title-${i}`} placeholder="Title" />
                <TextareaField value={s.description} onChange={(e) => updateService(i, "description", e.target.value)} placeholder="Description" />
              </div>
            ))}
          </EditorSection>

          <EditorSection title="About">
            <TextareaField value={content.about} onChange={(e) => updateField("about", e.target.value)} testId="ed-about" />
          </EditorSection>

          <EditorSection title="Contact">
            <FieldLabel>Email</FieldLabel>
            <Input value={content.contact_email || ""} onChange={(e) => updateField("contact_email", e.target.value)} testId="ed-cemail" />
            <FieldLabel>Phone</FieldLabel>
            <Input value={content.contact_phone || ""} onChange={(e) => updateField("contact_phone", e.target.value)} testId="ed-cphone" />
            <FieldLabel>Address</FieldLabel>
            <Input value={content.contact_address || ""} onChange={(e) => updateField("contact_address", e.target.value)} testId="ed-caddr" />
          </EditorSection>

          <EditorSection title="Brand colors">
            <div className="flex items-center gap-3">
              <input type="color" value={content.brand_primary} onChange={(e) => updateField("brand_primary", e.target.value)}
                className="w-12 h-12 rounded-lg border-2 border-black" />
              <span className="text-sm font-mono">{content.brand_primary}</span>
              <span className="text-xs text-gray-500">primary</span>
            </div>
            <div className="flex items-center gap-3 mt-3">
              <input type="color" value={content.brand_accent} onChange={(e) => updateField("brand_accent", e.target.value)}
                className="w-12 h-12 rounded-lg border-2 border-black" />
              <span className="text-sm font-mono">{content.brand_accent}</span>
              <span className="text-xs text-gray-500">accent</span>
            </div>
          </EditorSection>

          <EditorSection title="SEO">
            <FieldLabel>SEO title</FieldLabel>
            <Input value={content.seo_title} onChange={(e) => updateField("seo_title", e.target.value)} testId="ed-seotitle" />
            <FieldLabel>Meta description</FieldLabel>
            <TextareaField value={content.meta_description} onChange={(e) => updateField("meta_description", e.target.value)} testId="ed-meta" />
          </EditorSection>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <button onClick={() => setPreviewMode("desktop")} className={`${PILL} ${previewMode === "desktop" ? "bg-[#111] text-white" : "bg-white"}`}>Desktop</button>
            <button onClick={() => setPreviewMode("mobile")} className={`${PILL} ${previewMode === "mobile" ? "bg-[#111] text-white" : "bg-white"}`}>Mobile</button>
            <span className="text-xs text-gray-500 ml-auto">Live preview</span>
          </div>
          <div className="nb-card-flat overflow-hidden">
            <div className="bg-[#F5F0FF] border-b-2 border-black px-3 py-2 flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-400 border border-black" />
                <div className="w-3 h-3 rounded-full bg-yellow-400 border border-black" />
                <div className="w-3 h-3 rounded-full bg-green-400 border border-black" />
              </div>
              <div className="ml-3 text-xs font-mono truncate">{publicUrl}</div>
            </div>
            <div className={`preview-frame overflow-auto bg-white ${previewMode === "mobile" ? "max-w-[420px] mx-auto" : ""}`}
              style={{ height: "70vh" }} data-testid="site-preview">
              {isSrc && srcPayload ? (
                <SrcPreviewFrame
                  payload={srcPayload}
                  template={biz.industry_template}
                  slug={biz.slug}
                />
              ) : isSrc && !srcPayload ? (
                <div className="p-12 text-center text-sm text-gray-500" data-testid="src-preview-loading">
                  Loading preview…
                </div>
              ) : (
                <div className="p-12 text-center text-sm text-gray-500" data-testid="src-preview-unavailable">
                  Preview isn&apos;t available for this website.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
